// @flow
/*
 * Unprotected Goods and Services has some weird analytic page names.
 */

import * as fpti from '../../lib/analytics/fpti'
import { tracking } from '../../lib/analytics/mParticle'
import Events from '../../lib/analytics/eventTags'
import { getPreferredFundingSource } from './funding-option-utils'
import { PAYMENT_TYPE, transactionTypeMap } from '../../../../lib/constants'
import store from '../stores/configureStore'

const pageActionMap = {
  // start of guest specific analytics
  guestSelectFF: {
    link: 'chooseFF',
    extra: {
      fltp: 'f&f',
    },
  },
  guestSelectGS: {
    link: 'chooseGS',
    extra: {
      fltp: 'g&s',
    },
  },
  guestRedirectTesla: {
    link: 'signUpAndCreateAcct',
    extra: {},
  },
  guestSend: {
    link: 'Continue',
  },
  guestReview: {
    link: 'Agree and send money',
  },
  // end of guest specific analytics
  // pay request send button
  payRequestSend: {
    link: 'Send',
    extra: {},
  },
  // establish set of canonical field names
  paymentChoice: {
    extra: {},
  },
  sellerPaysFee: {
    link: 'sellerpays',
    extra: {},
  },
  senderPaysFee: {
    link: 'buyercovers',
    extra: {},
  },
  ffPreview: {
    extra: {},
  },
  gsPreview: {
    extra: {},
  },
  continue: {
    link: 'continue',
    extra: {},
  },
  selectFF: {
    link: 'friendsnfamily',
    eventData: () => {
      const { recipient } = store.getState()

      return Events.p2p_payment_type_toggle_pressed({
        protection_design: recipient.moneyPageOverPanel,
        payment_type: 'personal',
        suggested_protection_design: recipient.suggestedDesign,
      })
    },
    extra: {},
  },
  selectGS: {
    link: 'goodsnservices',
    type: 'cl',
    eventData: () => {
      const { recipient } = store.getState()

      return Events.p2p_payment_type_toggle_pressed({
        protection_design: recipient.moneyPageOverPanel,
        payment_type: 'purchase',
        suggested_protection_design: recipient.suggestedDesign,
      })
    },
    extra: {},
  },
  onFFchangePaymentType: {
    link: 'change',
    extra: {},
  },
  onGSchangePaymentType: {
    link: 'change',
    extra: {},
  },
  onFFclickNoteField: {
    link: 'note',
    extra: {},
  },
  onGSclickNoteField: {
    link: 'note',
    extra: {},
  },
  onClickSendPaymentNow: {
    link: 'Send Payment Now',
    extra: {},
  },
  purchaseProtection: {
    link: 'purchaseprotection',
    eventData: () => {
      const { recipient } = store.getState()

      return Events.p2p_payment_type_terms_pressed({
        protection_design: recipient.moneyPageOverPanel,
        suggested_protection_design: recipient.suggestedDesign,
      })
    },
    extra: {},
  },
  // checkbox variant
  enableProtection: {
    link: 'changeEnabled',
    extra: {},
  },
  disableProtection: {
    link: 'changeDisabled',
    extra: {},
  },
  paymentChoicePanelSend: {
    extra: {},
  },
  paymentChoicePanelBuy: {
    extra: {},
  },
  eligiblePurchases: {
    link: 'eligiblepurchases',
    extra: {},
  },
}

export function getUnifiedSendAnalyticInfo(pageAction: string) {
  return pageActionMap[pageAction]
}

export function trackImpressionUnifiedSend(
  pageAction: string,
  extraAnalytics: any = {}
) {
  const { extra } = pageActionMap[pageAction]
  fpti.trackImpression({
    ...extra,
    flfr: window.flfr,
    ...extraAnalytics,
  })
}

export function trackCancelBtn() {
  const { recipient } = store.getState()
  tracking(
    Events.p2p_payment_type_cancel_pressed({
      suggested_protection_design: recipient.suggestedDesign,
      protection_design: recipient.moneyPageOverPanel,
    })
  )
  fpti.trackLinkWithData('cancel', {}, 'link', {})
}

export function trackAmountPageImpression(
  flowType: string,
  recipient: string,
  data: any = {}
) {
  /* This is to track the recurring type for protections experiment. This
    does not include the external flow.
  */
  const { external, recipient: receiver } = store.getState()
  if (!external.env && receiver) {
    data.recurring_type = receiver.recurringType || 'none'
    data.rcvr_acct_type = receiver.recipientAccountType || ''
  }
  fpti.trackImpression(data)
}

export function trackAmountPageClick(
  flowType: string,
  recipient: string,
  linkName: string
) {
  fpti.trackLinkWithData(linkName, {}, 'link', {})
}

export function trackClickUnifiedSend(
  pageAction: string,
  extraData: Object = {}
) {
  const { link, extra, eventData } = pageActionMap[pageAction]
  if (typeof eventData === 'function') {
    // eventData property recently added for mParticle data or any other future tracking sdk
    tracking(eventData())
  }
  fpti.trackLinkWithData(link, Object.assign(extra, extraData), 'link', {})
}

export function trackClick3ds2(page: string, extraAnalytics: any = {}) {
  const { link } = pageActionMap[page]
  fpti.trackLinkWithData(link, extraAnalytics, 'link', {})
}

/* Track payment choice, page and pgrp changes depending on current page
 * Please see section titled: Clicking on 'Change Payment Type' button
 * https://engineering.paypalcorp.com/confluence/pages/viewpage.action?pageId=375228475
 */
export function trackGuestClickPmtChoice() {
  const linkName = 'ChangePmtType'

  fpti.trackLinkWithData(linkName, {}, 'link', {})
}

/* Tracks when guest returns to page after selecting payment choice
 * Please see section titled: Return to Guest Start Page
 * https://engineering.paypalcorp.com/confluence/pages/viewpage.action?pageId=375228475
 *
 */
export function trackGuestReturnToPagePmtChoice(pageAction: string) {
  const { link, extra } = pageActionMap[pageAction]

  const data = {
    pglk: link,
  }

  if (extra.fltp) {
    fpti.setPersistentBeaconData('fltp', extra.fltp)
  }

  fpti.trackImpression(data)
}

export function getAnalytics({
  selectedFundingOption,
  fundingOptions,
  flowType = '',
}: {
  selectedFundingOption: FundingOption,
  fundingOptions: FundingOption[],
  flowType: string,
}) {
  const preferred = getPreferredFundingSource(fundingOptions) || { id: '' }
  const selectedFOExists = !!Object.keys(selectedFundingOption).length
  const dflFmxId =
    selectedFOExists &&
    selectedFundingOption.funding_sources.map(source => source.id)
  const usingPreferred =
    selectedFOExists &&
    selectedFundingOption.funding_sources.some(source => source.isPreferred)

  const paymentType = transactionTypeMap[flowType]
  const fltp = paymentType === PAYMENT_TYPE.PURCHASE ? 'g&s' : 'f&f'
  const dfl_fmxid = dflFmxId ? dflFmxId.toString() : 'nodefault'
  const pref_fmxid = preferred.id || 'notset'
  const is_pref = usingPreferred ? 'Y' : 'N'
  // indicating user lands on preview page without coming from funding option selection page
  const flfr = 'start'

  return { fltp, dfl_fmxid, pref_fmxid, is_pref, flfr }
}
